export type Valuation = {
    id: number;
    valuationDate: string;
    valuationTypeId: number;
    valuationType: string;
    lowerRangeAmount: number;
    upperRangeAmount: number;
    currencyCode: string;
    valuerType?: string;
    valuerUserId: number;
    valuerName: string;
    valuerOrganisationId?: number | null;
    valuerOrganisationName?: string | null;
    externalValuer: string | null;
};

export type ValuationType = {
    id: number;
    type: string;
    description: string;
}

export type ValuerOption = {
    userId: number;
    displayName: string;
}

export interface ValuationCard {
    date: string;
    valuation: string;
    currency: string;
    baseCurrencyValue: string | undefined;
    valuationType: string;
    valuer: string;
    externalValuer: string | null;
}

export type exchangeRate = {
    [key: string]: number
}
export interface ValuationHeaderData {
    artworkId?: number | null;
    acquisition?: Valuation | null;
    insurance?: Valuation | null;
    fairMarketValue?: Valuation | null;
    exchangeRates?: exchangeRate | null;
} 

export interface ArtworkBaseValuation {
    artworkId: number;
    acquisition?: number | null;
    insurance?: number | null;
    fairMarketValue?: number | null;
    baseCurrency?: string | null;
}

export interface ValuationSummaryDto {
    valuationTypeId: number;
    valuationType?: string | null;
    currencyCode: string;
    totalLowerRangeAmount: number;
    totalUpperRangeAmount: number;
    artworkCount: number;
}

export interface ValuationTypeTotal {
    valuationTypeId: number;
    valuationType: string;
    totalLowerConverted: number;
    totalUpperConverted: number;
}

export interface UseValuationSummaryResult {
    rawSummaries: ValuationSummaryDto[];
    totalsPerType: ValuationTypeTotal[];
    grandTotalLowerConverted: number;
    grandTotalUpperConverted: number;
    isLoadingSummaries: boolean;
    summaryError: string | null;
    refetch: () => void;
}

export interface PortfolioValuationSummaryDto {
    totalArtworks: number;
    artworksWithValuations: number;
    acquisitionSummary: ValuationSummaryDto[];
    insuranceSummary: ValuationSummaryDto[];
    fairMarketValueSummary: ValuationSummaryDto[];
}

//Summaries
export type cardType = "acquisition" | "insurance" | "fairMarketValue";
export interface ValuationSummaryCardData {
    title: string;
    type: cardType;
    currencyCode: string;
    lowerRange: number;
    upperRange: number;
    artworkCount: number;
}

export interface ValuationCurrencySummary {
    currencyCode: string;
    totalLowerRangeAmount: number;
    totalUpperRangeAmount: number;
    artworkCount: number;
}

export interface PortfolioValuationSummary {
    totalArtworks: number;
    artworksWithValuations: number;
    acquisitionSummary: ValuationCurrencySummary[];
    insuranceSummary: ValuationCurrencySummary[];
    fairMarketValueSummary: ValuationCurrencySummary[];
}

export interface ConvertedValuationSummary {
    baseCurrency: string;
    totalLowerRange: number;
    totalUpperRange: number;
    artworkCount: number;
    originalSummaries: ValuationCurrencySummary[];
}

export interface PortfolioValuationBaseCurrency {
    baseCurrency: string;
    totalArtworks: number;
    artworksWithValuations: number;
    acquisition: ConvertedValuationSummary;
    insurance: ConvertedValuationSummary;
    fairMarketValue: ConvertedValuationSummary;
}

// Conversion function for frontend
export function convertValuationToBaseCurrency(
    valuationSummaries: ValuationCurrencySummary[],
    baseCurrency: string,
    conversionRates: Record<string, number>
): ConvertedValuationSummary {
    let totalLower = 0;
    let totalUpper = 0;
    let artworkCount = 0;

    valuationSummaries.forEach(summary => {

        const fromRate = conversionRates[summary.currencyCode] || 1;
        const toRate = conversionRates[baseCurrency] || 1;
        const convertedLower = (summary.totalLowerRangeAmount / fromRate) * toRate;
        const convertedUpper = (summary.totalUpperRangeAmount / fromRate) * toRate;

        const rate = summary.currencyCode === baseCurrency
            ? 1
            : conversionRates[summary.currencyCode];

        totalLower += convertedLower;
        totalUpper += convertedUpper;
        artworkCount += summary.artworkCount;
    });

    return {
        baseCurrency,
        totalLowerRange: totalLower,
        totalUpperRange: totalUpper,
        artworkCount,
        originalSummaries: valuationSummaries
    };
}

// Main function to convert entire portfolio
export function convertPortfolioToBaseCurrency(
    portfolioSummary: PortfolioValuationSummary,
    baseCurrency: string,
    conversionRates: Record<string, number>
): PortfolioValuationBaseCurrency {

    return {
        baseCurrency,
        totalArtworks: portfolioSummary.totalArtworks,
        artworksWithValuations: portfolioSummary.artworksWithValuations,
        acquisition: convertValuationToBaseCurrency(
            portfolioSummary.acquisitionSummary,
            baseCurrency,
            conversionRates
        ),
        insurance: convertValuationToBaseCurrency(
            portfolioSummary.insuranceSummary,
            baseCurrency,
            conversionRates
        ),
        fairMarketValue: convertValuationToBaseCurrency(
            portfolioSummary.fairMarketValueSummary,
            baseCurrency,
            conversionRates
        )
    };
}

export function convertArtworkDetailsToBaseCurrency(
    artworkValuations: ValuationHeaderData[],
    baseCurrency: string,
    conversionRates: Record<string, number>
): ArtworkBaseValuation[] {
    return artworkValuations.map(artworkValuation => {
        let convertedAcq = 0;
        let convertedIns = 0;
        let convertedFMV = 0;

        // Helper function to convert a single valuation
        const convertValuation = (valuation: Valuation | null | undefined): number => {
            if (!valuation || !valuation.lowerRangeAmount || !valuation.currencyCode) {
                return 0;
            }

            const fromRate = conversionRates[valuation.currencyCode] || 1;
            const toRate = conversionRates[baseCurrency] || 1;

            return (valuation.lowerRangeAmount / fromRate) * toRate;
        };

        // Convert each valuation
        convertedAcq = convertValuation(artworkValuation.acquisition);
        convertedIns = convertValuation(artworkValuation.insurance);
        convertedFMV = convertValuation(artworkValuation.fairMarketValue);

        // Return new object with converted values
        return {
            artworkId: artworkValuation.artworkId ?? 0,
            acquisition: convertedAcq,
            insurance: convertedIns,
            fairMarketValue: convertedFMV,
            baseCurrency: baseCurrency
        };
    });
}